/**
 * Fetches the account-level "meeting rooms enabled" admin setting.
 * GET /api_ats/v1/meeting_rooms/settings
 *   → { account_setting: { meeting_rooms_enabled: boolean } }
 *
 * Gates the "Book a meeting room" checkbox in ADHOC scheduling, where
 * there's no interview plan to carry the flag. Plan / edit flows read
 * `meeting_rooms_enabled` straight off `interviewPlan`; adhoc has no plan,
 * so it hits this settings endpoint directly. Same underlying value
 * (`Account.current.account_setting.meeting_rooms_enabled`) either way.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { api } from '@/api/request';

interface MeetingRoomsSettingsResponse {
  account_setting?: { meeting_rooms_enabled?: boolean };
}

export function useMeetingRoomsAccountSetting(options?: {
  enabled?: boolean;
}): UseQueryResult<boolean, Error> {
  return useQuery({
    queryKey: ['schedule-interview', 'meeting-rooms-account-setting'],
    queryFn: async () => {
      // Default unwrapData=true strips the `{ data: {...} }` envelope.
      const raw = await api.get<MeetingRoomsSettingsResponse>(
        'meeting_rooms/settings'
      );
      return Boolean(raw?.account_setting?.meeting_rooms_enabled);
    },
    enabled: options?.enabled ?? true,
    // Account-level admin setting — rarely changes within a session.
    staleTime: 5 * 60 * 1000,
  });
}
